"use client"

import { useActionState, useEffect } from "react";
import { createFeedback } from "./actions";
import { State, initialState } from "@/types/forms";
import { Form, Input, Select, TextArea } from "@/app/ui/molecular/formFields";
import { useFormDraft } from "@/app/lib/hooks/useFormDraft";

type FeedbackDraft = {
  value: string;
  valueNps: string;
  wouldInvest: string;
  previousInvestment: string;
  launchStartup: string;
  email: string;
  zip: string;
  blueprint: string;
  otherThoughts: string;
};

export default function FeedbackForm() {
  const [state, formAction] = useActionState<State, FormData>(createFeedback, initialState);

  const { values, updateField, saveNow, clearDraft, hasDraft, lastSaved } = useFormDraft<FeedbackDraft>(
    "feedback_form",
    {
      value: "",
      valueNps: "",
      wouldInvest: "",
      previousInvestment: "",
      launchStartup: "",
      email: "",
      zip: "",
      blueprint: "",
      otherThoughts: ""
    }
  );

  useEffect(() => {
    if (state.message === "SUCCESS") {
      clearDraft();
    } else if (state.errors) {
      saveNow();
    }
  }, [state.message, state.errors, clearDraft, saveNow]);

  if (state.message === "DEPLOYMENT_MISMATCH") {
    return (
      <div className="w-full max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-6 sm:py-8 lg:py-12">
        <div className="relative rounded-2xl shadow-xl overflow-hidden p-8 text-center border border-white/10 bg-gradient-to-b from-(--purple)/95 to-(--dark-purple)">
          <p className="text-white text-lg mb-4">
            This page has been updated since you last visited.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="font-bold rounded-full py-3 px-8 text-lg transition-all hover:scale-105 text-(--dark-purple) bg-gradient-to-r from-(--brand) to-(--gold) hover:opacity-90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <Form
      name="Feedback"
      action={formAction}
      error={state.message && state.message !== "" && !state.message.includes("DEPLOYMENT") ? state.message : undefined}
      hasDraft={hasDraft}
      lastSaved={lastSaved}
      onClearDraft={clearDraft}
    >

      <Select
        id="value"
        label="I agree with the values of EconChangers:"
        defaultValue={values.value}
        required
        placeholderOption="Please select..."
        errors={state.errors?.value}
        onChange={(e) => updateField("value", e.target.value)}
        options={[
          {
            value:"0",
            label:"No"
          },{
            value:"1",
            label:"Yes"
          }              
        ]}
      />

      <Select
        id="valueNps"
        label="I agree with their values this strongly:"
        defaultValue={values.valueNps}
        required
        placeholderOption="Please select..."
        errors={state.errors?.valueNps}
        onChange={(e) => updateField("valueNps", e.target.value)}
        options={[
          {
            value:"1",
            label:"1 - Least"
          },{
            value:"2",
            label:"2"
          },{
            value:"3",
            label:"3"
          },{
            value:"4",
            label:"4"
          },{
            value:"5",
            label:"5 - Most"
          }
        ]}
      />

      <Select
        id="wouldInvest"
        label="I would invest in an EconChangers Co-Op:"
        defaultValue={values.wouldInvest}
        required
        placeholderOption="Please select..."
        errors={state.errors?.wouldInvest}
        onChange={(e) => updateField("wouldInvest", e.target.value)}
        options={[
          {
            value:"no",
            label:"No"
          },{
            value:"yes",
            label:"Yes"
          },{
            value:"maybe",
            label:"Maybe"
          }         
        ]}
      />

      <Select
        id="previousInvestment"
        label="I have invested into a startup before:"
        defaultValue={values.previousInvestment}
        required
        placeholderOption="Please select..."
        errors={state.errors?.previousInvestment}
        onChange={(e) => updateField("previousInvestment", e.target.value)}
        options={[
          {
            value:"0",
            label:"No"
          },{
            value:"1",
            label:"Yes"
          }              
        ]}
      />

      <Select
        id="launchStartup"
        label="I would launch an EconChangers Co-Op:"
        defaultValue={values.launchStartup}
        required
        placeholderOption="Please select..."
        errors={state.errors?.launchStartup}
        onChange={(e) => updateField("launchStartup", e.target.value)}
        options={[
          {
            value:"no",
            label:"No"
          },{
            value:"yes",
            label:"Yes"
          },{
            value:"maybe",
            label:"Maybe"
          }         
        ]}
      />

      <Input
        id="email"
        type="email"
        label="Email:"
        placeholder="enter email: not required"
        errors={state.errors?.email}
        maxLength={100}
        autoComplete="email"
        defaultValue={values.email}
        onChange={(e) => updateField("email", e.target.value)}
      />

      <Input
        id="zip"
        type="text"
        label="Zip Code:"
        placeholder="enter zip code: not required"
        maxLength={5}
        minLength={5}
        inputMode="numeric"
        pattern="[0-9]{5}"
        filterInput={(v) => v.replace(/\D/g, "").slice(0, 5)}
        autoComplete="postal-code"
        errors={state.errors?.zip}
        defaultValue={values.zip}
        onChange={(e) => updateField("zip", e.target.value)}
      />

      <Input
        id="blueprint"
        type="text"
        label="Co-op I would like to launch/support:"
        placeholder="enter industry: not required"
        maxLength={100}
        errors={state.errors?.blueprint}
        defaultValue={values.blueprint}
        onChange={(e) => updateField("blueprint", e.target.value)}
      />

      <TextArea
        id="otherThoughts"
        label="Other Thoughts:"
        placeholder="enter comments: not required"
        maxLength={240}
        errors={state.errors?.otherThoughts}
        defaultValue={values.otherThoughts}
        onChange={(e) => updateField("otherThoughts", e.target.value)}
      />
    </Form>
  );
}
